import React, { JSX, useState } from 'react'
import { useAuth } from 'context/user'
import { LoadingPage } from 'components/Loading'
import { Dropdown } from 'components/Dropdown'
import { AnalyticsMetafield } from 'types/api'
import { EmptyDataCard } from 'components/EmptyDataCard'
import { SalesTableSection } from 'components/SalesTable'
import { moneyFormatter, numberFormatter } from 'lib/helpers'
import { Chart } from 'components/Chart'
import { chartIcon, tableIcon } from 'components/Icons'
import { HourlyTable } from 'components/HourlyTable'
import { TimeRangeDropdown } from 'components/TimeRangeDropdown'
import { MultiSelectDropdown } from 'components/MultiSelectDropdown'
import { WorldMap } from 'components/WorldMap'
import { ProductsLeaderboard } from 'components/ProductsLeaderboardSection'
import { TotalsSection } from 'components/TotalsSection'
import { RefreshButton } from 'components/RefreshButton'
import { useIsFetching } from '@tanstack/react-query'
import { ReferralPlatformLeaderboard } from 'components/ReferralPlatformLeaderboard'
import { UTMLeaderBoard } from 'components/UTMLeaderboard'
import { ReferringSitesLeaderboard } from 'components/ReferringSitesLeaderboard'
import { CustomerStatisticsSection } from 'components/CustomerStatisticsSection'
import { TopCustomerData } from 'components/TopCustomersLeaderboard'
import { usePersistentAnalyticsFilters } from 'hooks/usePersistentAnalyticsFilters'
import { QuickRangePreset, getQuickRange } from 'lib/timeRanges'

const defaultAll = { id: 'all', title: 'All' }
export interface FiltersState {
  project?: AnalyticsMetafield
  productOption: AnalyticsMetafield[]
  bundleOption: AnalyticsMetafield[]
  range: {
    startDate: Date
    endDate: Date
    preset?: QuickRangePreset
  }
}

const defaultFilters: FiltersState = {
  productOption: [defaultAll],
  bundleOption: [defaultAll],
  project: defaultAll,
  range: { ...getQuickRange('365d'), preset: '365d' }
}

export const TotalsByEventPage = ():(JSX.Element | null) => {
  const { selectedClient, auth0Token, analyticsUser, setSelectedClientId, clientOptions } = useAuth()
  const role = analyticsUser?.role
  const [filters, setFilters] = usePersistentAnalyticsFilters(defaultFilters)
  const [viewType, setViewType] = useState<'graphic' | 'tabular'>('graphic')
  const isFetching = useIsFetching()
  const refreshing = isFetching > 0

  const updateFilter = <K extends keyof FiltersState>(key: K, value: FiltersState[K]): void => {
    setFilters({ ...filters, [key]: value })
  }

  const handleClientSwitch = ({ id }: AnalyticsMetafield): void => {
    setSelectedClientId(id.toString())
    setFilters({
      ...defaultFilters,
      range: filters.range
    })
  }

  const graphBlock = (
    <>
      <div className='mt-4 md:mx-0 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden relative px-2 py-4 sm:p-6'>
        <Chart
          token={auth0Token}
          client={selectedClient}
          filters={filters}
          dataKey='sales'
          secondaryDataKey='quantity'
          formatter={moneyFormatter}
          secondaryFormatter={numberFormatter}
          height='100%'
          width='100%'
        />
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 md:mx-0'>
        <ProductsLeaderboard
          products={clientOptions?.products}
          bundles={clientOptions?.bundles}
          token={auth0Token}
          client={selectedClient}
          filters={filters}
        />

        <div className='shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden p-4 min-h-[280px] flex flex-col'>
          <HourlyTable
            filters={filters}
            client={selectedClient}
            token={auth0Token}
          />
        </div>
      </div>
      <div className='mt-4 md:mx-0 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl overflow-hidden relative px-2 py-4 sm:p-6'>
        <WorldMap
          filters={filters}
          client={selectedClient}
          token={auth0Token}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 md:mx-0 '>
        <CustomerStatisticsSection filters={filters} client={selectedClient} token={auth0Token} />
        <TopCustomerData role={role} filters={filters} client={selectedClient} token={auth0Token} />
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 md:mx-0'>
        <ReferringSitesLeaderboard filters={filters} client={selectedClient} token={auth0Token} />
        <ReferralPlatformLeaderboard filters={filters} client={selectedClient} token={auth0Token} />
        <UTMLeaderBoard filters={filters} client={selectedClient} token={auth0Token} />
      </div>
    </>
  )

  const tableBlock = <SalesTableSection products={clientOptions?.products} bundles={clientOptions?.bundles} role={role} filters={filters} client={selectedClient} token={auth0Token} />
  const viewContent = viewType === 'graphic' ? graphBlock : tableBlock
  const toggleViewType = (): void => setViewType(viewType === 'graphic' ? 'tabular' : 'graphic')
  const viewToggleTitle = viewType === 'graphic' ? 'Tabular' : 'Graphic'

  const viewToggleIcon = viewType === 'graphic' ? tableIcon : chartIcon

  if (analyticsUser === null || analyticsUser.clients.length === 0) return <EmptyDataCard message='Your data is not yet available.' />
  if (selectedClient === null || clientOptions === undefined) return <LoadingPage />
  return (
    <div className='flex w-full flex-col font-normal'>
      <div className='px-4 mt-4 sm:px-6 lg:px-8 pb-6 shadow-sm dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 rounded-none md:rounded-2xl md:shadow-none md:dark:shadow-none md:ring-0 dark:md:ring-0'>
        <div className='flex flex-col items-start'>
          <div className='w-full sm:mt-4 grid grid-cols-2 md:flex gap-2 md:gap-4 text-xs'>
            <div className='col-span-2 sm:flex-1'>
              Range
              <TimeRangeDropdown
                disabled={refreshing}
                start={filters.range.startDate}
                end={filters.range.endDate}
                onRangeSelect={(start, end, preset) =>
                  updateFilter('range', { startDate: start, endDate: end, preset })}
              />
            </div>
            <div className='col-span-2 sm:flex-1'>
              Client
              <Dropdown
                disabled={refreshing}
                testId='client' placeholderText='Client'
                setSelected={handleClientSwitch} selected={selectedClient} data={analyticsUser.clients}
              />
            </div>
            <div className='flex-1'>
              Project
              <Dropdown
                disabled={refreshing}
                testId='project'
                placeholderText='Event group'
                setSelected={(val) => updateFilter('project', val)}
                selected={filters.project}
                data={clientOptions.projects}
                allOption
              />
            </div>
            <div className='flex-1'>
              Product
              <MultiSelectDropdown
                disabled={refreshing}
                testId='product'
                type='Product'
                data={[...clientOptions.products, ...clientOptions.bundles]}
                selected={filters.productOption}
                setSelected={(val) => updateFilter('productOption', val)}
                allOption
              />
            </div>
            <div className='col-span-2 sm:col-span-1'>
              &nbsp;

              <div className='flex gap-2 text-white'>
                <RefreshButton />
                <button
                  disabled={refreshing}
                  type='button'
                  data-testid='toggle-view-icon'
                  title={viewToggleTitle}
                  onClick={toggleViewType}
                  className='flex-1 rounded-md bg-brand p-2 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 hover:bg-brand/70'
                >
                  {viewToggleIcon}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='pb-6 sm:px-6 lg:px-8 relative'>
        <TotalsSection filters={filters} token={auth0Token} client={selectedClient} />
        <div />
        {viewContent}
      </div>
    </div>
  )
}
